import { useDispatch, useSelector } from 'react-redux';
import {
    IntegratedModel,
    CaseInfo,
    GenerationType,
    AskStreamCustomInstructions,
    ResponseDepth,
    MessageInfo,
    MessageSenderType,
    SingleChatHistoryData,
    AskStreamHandlers
} from '../types';
import {
    useState,
    createContext,
    useContext,
    ReactNode,
    Dispatch,
    SetStateAction,
    FC,
    useEffect
} from 'react';
import {
    useAppContext,
    useMessages,
    useModel,
    useProductDetails,
    useSelectedMessage,
    useSummaryChatUserQuestion,
    useUserData
} from '../AppContext';
import {
    useChatContext,
    useHistoryMessages,
    useMessageDisclaimers,
    useRegenerate
} from './ChatContext';
import Backend from '../backend/Backend';
import { consoleLog, getTodayDateAndTime } from '../utils/utils';
import useAnalytics from '../hooks/useAnalytics';
import { useTranslation } from 'react-i18next';
import { setTriageChatId } from '../actions/coPilotActions';
import {
    ANALYTICS_EVENTS,
    HEADER_MARKER_BEGIN,
    HEADER_MARKER_END,
    METADATA_MARKER_BEGIN,
    METADATA_MARKER_END,
    SCOP_ERROR_RESPONSE_MARKER
} from '../consts';

interface AskStreamContextValue {
    resetAskStreamContext: () => void;
    reader: ReadableStreamDefaultReader;
    setReader: Dispatch<SetStateAction<ReadableStreamDefaultReader>>;
    isStreamStopped: boolean;
    setIsStreamStopped: Dispatch<SetStateAction<boolean>>;
    isStreamInProgress: boolean;
    setIsStreamInProgress: Dispatch<SetStateAction<boolean>>;
    isAskRequestInProgress: boolean;
    setIsAskRequestInProgress: Dispatch<SetStateAction<boolean>>;
    abortFunction: AbortController;
    setAbortFunction: Dispatch<SetStateAction<AbortController>>;
    typingStarted: boolean;
    setTypingStarted: Dispatch<SetStateAction<boolean>>;
    isStreamError: boolean;
    setIsStreamError: Dispatch<SetStateAction<boolean>>;
    cancelStream: () => void;
    generationType: GenerationType;
    setGenerationType: Dispatch<SetStateAction<GenerationType>>;
}

const AskStreamContext = createContext<AskStreamContextValue | null>(null);

export const AskStreamContextProvider: FC<{ children: ReactNode }> = ({
    children
}) => {
    const [isAskRequestInProgress, setIsAskRequestInProgress] =
        useState<boolean>(false);
    const [isStreamInProgress, setIsStreamInProgress] =
        useState<boolean>(false);
    const [abortFunction, setAbortFunction] = useState<AbortController>(null);
    const [reader, setReader] = useState<ReadableStreamDefaultReader>();
    const [isStreamStopped, setIsStreamStopped] = useState<boolean>(false);
    const [typingStarted, setTypingStarted] = useState<boolean>(false);
    const [isStreamError, setIsStreamError] = useState<boolean>(false);
    const [generationType, setGenerationType] = useState<GenerationType>(
        GenerationType.SemanticOnly
    );

    const { resetCopilot } = useSelector((state: any) => state.coPilotReducer);

    const resetAskStreamContext = () => {
        cancelStream();
        setIsStreamError(false);
        setIsStreamStopped(false);
        setGenerationType(GenerationType.SemanticOnly);
    };

    const cancelStream = () => {
        reader?.cancel();
        abortFunction?.abort();
        setIsStreamInProgress(false);
        setIsAskRequestInProgress(false);
        setTypingStarted(false);
    };

    useEffect(() => {
        if (resetCopilot) {
            resetAskStreamContext();
        }
    }, [resetCopilot]);

    const value: AskStreamContextValue = {
        resetAskStreamContext,
        reader,
        setReader,
        isStreamStopped,
        setIsStreamStopped,
        isStreamInProgress,
        setIsStreamInProgress,
        isAskRequestInProgress,
        setIsAskRequestInProgress,
        abortFunction,
        setAbortFunction,
        typingStarted,
        setTypingStarted,
        isStreamError,
        setIsStreamError,
        cancelStream,
        generationType,
        setGenerationType
    };

    return (
        <AskStreamContext.Provider value={value}>
            {children}
        </AskStreamContext.Provider>
    );
};

export const useAskStreamContext = (): AskStreamContextValue => {
    const context = useContext(AskStreamContext);

    if (!context) {
        throw new Error('Did you forget to use the Provider?');
    }

    return context;
};

export const useAskStream = () => {
    const {
        setReader,
        setIsStreamStopped,
        setIsStreamInProgress,
        setIsAskRequestInProgress,
        setAbortFunction,
        setTypingStarted,
        setIsStreamError,
        cancelStream,
        isStreamInProgress,
        setGenerationType,
        generationType
    } = useAskStreamContext();
    const { responseDepth } = useUserData();
    const { chatId } = useSelector((state: any) => state.coPilotReducer);
    const { model, modelSerialNumber } = useModel();
    const { productDetails } = useProductDetails();
    const { t } = useTranslation();
    const { sendEvent } = useAnalytics();
    const { scopSettings } = useAppContext();
    const { fetchMessageDisclaimers } = useMessageDisclaimers();
    const { setShowSuggestedQuestions } = useChatContext();
    const { historyMessages, setHistoryMessages } = useHistoryMessages();
    const { messages, setMessages } = useMessages();
    const { updateChatSummary } = useSummaryChatUserQuestion();

    const dispatch = useDispatch();

    const { setSelectedMessage } = useSelectedMessage();
    const { setIsRegenerateAvailable } = useRegenerate();

    const { investigationId } = useSelector(
        (state: any) => state.deviceInspectionReducer
    );
    const { AssetID } = useSelector(
        (state: any) => state.integrationReducer.integrationTokens
    );
    const { workOrder } = useSelector((state: any) => state.workOrderReducer);

    const askAndStream = async (
        modelName: string,
        question: string,
        returnAllResults: boolean,
        regenerate: boolean,
        previousGenerationType: GenerationType | null,
        skipQuestionPp: boolean = false,
        selectedProductDetails: IntegratedModel,
        newResponseDepth: ResponseDepth = responseDepth,
        preventHistorySave: boolean = false,
        handlers: AskStreamHandlers
    ) => {
        const newAbortController = new AbortController();
        try {
            handlers?.onAskInit(newAbortController);
            const caseInfo = {
                investigation_id: investigationId,
                work_order_id: workOrder,
                asset_id: AssetID
            } as CaseInfo;

            const customInstructions = {
                response_depth:
                    scopSettings.allowUserToControlResponseLength &&
                    newResponseDepth === ResponseDepth.Concise
                        ? newResponseDepth
                        : undefined
            } as AskStreamCustomInstructions;

            const response = await Backend.askStream(
                modelName,
                question,
                returnAllResults,
                chatId,
                regenerate,
                previousGenerationType,
                skipQuestionPp,
                newAbortController.signal,
                selectedProductDetails?.productIds?.productId,
                caseInfo,
                customInstructions,
                preventHistorySave,
                modelSerialNumber
            );
            const reader = response.body.getReader();
            handlers?.onStreamInit(reader);
            if (response.status !== 200) {
                handlers.handleAnswer(' ');
                throw new Error(response.statusText);
            }

            while (true) {
                const { value, done } = await reader.read();
                if (done) {
                    break;
                }
                let str = new TextDecoder().decode(value);
                handlers.handleAnswer(str);
            }
        } catch (ex: any) {
            if (ex.name === 'AbortError') {
                consoleLog('Stream is aborted');
                return;
            }
            handlers?.onError(ex);
            console.error('Failed to steam answer: ', ex.message);
            window.sentry.log(ex);
        }
    };

    const askStream = async (
        question: string,
        selectedModel = null,
        newMessages,
        regenerate = false,
        useOriginalValue: boolean = false,
        modelDisplayValue = selectedModel || '',
        askAnyway: boolean = false,
        selectedProductDetails: IntegratedModel = null,
        newResponseDepth?: ResponseDepth
    ) => {
        if (isStreamInProgress && !askAnyway) {
            return;
        }
        let productDetailsToUse = productDetails;
        if (!productDetailsToUse && selectedProductDetails) {
            productDetailsToUse = selectedProductDetails;
        }
        let latestMessage: MessageInfo = {
            chatID: '',
            rowID: '',
            message: '',
            questionId: '',
            sentTime: Date.now().toString(),
            direction: 'incoming',
            sender: MessageSenderType.Aquant,
            sourceDocuments: [],
            relatedSourceDocuments: [],
            condensedQuestion: '',
            canProvideFeedback: false,
            triageLink: null,
            triageSolutionCount: 0,
            siLink: null,
            promptInfo: {},
            model:
                productDetails?.modelDisplay ||
                productDetails?.modelName ||
                modelDisplayValue,
            productDetails: productDetailsToUse,
            expertDetails: null
        };
        if (regenerate) {
            latestMessage = newMessages.slice(-1)[0];
            latestMessage.message = '';
            latestMessage.rowID = '';
            latestMessage.sentTime = Date.now().toString();
            setMessages([...newMessages.slice(0, -1), latestMessage]);
        } else {
            newMessages.push(latestMessage);
            setMessages([...newMessages]);
        }

        let __headersData__ = '';
        let __metadata__ = '';
        let metadataReady = false;

        function handleAnswer(answer: string) {
            if (answer.includes(SCOP_ERROR_RESPONSE_MARKER)) {
                setIsStreamInProgress(false);
                setIsAskRequestInProgress(false);
                setTypingStarted(false);
                setIsRegenerateAvailable(true);
                setIsStreamError(true);
                if (!latestMessage.message) {
                    latestMessage.message = ' ';
                    setMessages([...newMessages]);
                }
                return;
            }
            if (
                answer.startsWith(HEADER_MARKER_BEGIN) &&
                answer.includes(HEADER_MARKER_END)
            ) {
                __headersData__ = answer.substring(
                    HEADER_MARKER_BEGIN.length,
                    answer.indexOf(HEADER_MARKER_END)
                );
                let headersdataObj = JSON.parse(__headersData__);
                if (
                    headersdataObj['prompt_info'] &&
                    headersdataObj['prompt_info'].suggested_alternatives
                        ?.length > 0
                ) {
                    setShowSuggestedQuestions(true);
                    latestMessage.promptInfo = headersdataObj['prompt_info'];
                }
                answer = answer.substring(
                    answer.indexOf(HEADER_MARKER_END) +
                        HEADER_MARKER_END.length,
                    answer.length
                );
                latestMessage.expertDetails = headersdataObj['expert_details'];
            }
            if (
                answer.startsWith(METADATA_MARKER_BEGIN) &&
                answer.endsWith(METADATA_MARKER_END)
            ) {
                __metadata__ = answer.substring(
                    METADATA_MARKER_BEGIN.length,
                    answer.length - METADATA_MARKER_END.length
                );
                metadataReady = true;
            } else if (
                answer.includes(METADATA_MARKER_BEGIN) &&
                answer.endsWith(METADATA_MARKER_END)
            ) {
                const partOfAnswer = answer.substring(
                    0,
                    answer.indexOf(METADATA_MARKER_BEGIN)
                );
                setTypingStarted(true);
                latestMessage.message += partOfAnswer;
                setMessages([...newMessages]);
                __metadata__ = answer.substring(
                    partOfAnswer.length + METADATA_MARKER_BEGIN.length,
                    answer.length - METADATA_MARKER_END.length
                );
                metadataReady = true;
            } else if (answer.startsWith(METADATA_MARKER_BEGIN)) {
                __metadata__ = answer.substring(METADATA_MARKER_BEGIN.length);
            } else if (answer.includes(METADATA_MARKER_BEGIN)) {
                const partOfAnswer = answer.substring(
                    0,
                    answer.indexOf(METADATA_MARKER_BEGIN)
                );
                __metadata__ = answer.substring(
                    partOfAnswer.length + METADATA_MARKER_BEGIN.length
                );
                setTypingStarted(true);
                latestMessage.message += partOfAnswer;
                setMessages([...newMessages]);
            } else if (answer.endsWith(METADATA_MARKER_END)) {
                __metadata__ += answer.substring(
                    0,
                    answer.length - METADATA_MARKER_END.length
                );
                metadataReady = true;
            } else if (__metadata__.length > 0) {
                __metadata__ += answer;
            }

            if (metadataReady) {
                let canProvideFeedback = false;
                let metadataObj = JSON.parse(__metadata__);
                let answer = metadataObj['answer'];
                let condensedQuestion = metadataObj['condensed_question'];
                let sourceDocuments = [];
                let relatedSourceDocuments = [];
                let sdocs = metadataObj['source_documents'];
                latestMessage.rowID = metadataObj['chat_history_line_id'];
                if (
                    (sdocs && sdocs.length > 0) ||
                    (metadataObj?.['answer_sources'] &&
                        metadataObj?.['answer_sources'].length > 0)
                ) {
                    sourceDocuments = (sdocs || []).map(
                        (sd: any, index: number) => {
                            return {
                                content: sd.page_content,
                                metadata: sd.metadata,
                                messageId: latestMessage.rowID,
                                id: latestMessage.rowID + index,
                                tenantModel: model
                            };
                        }
                    );
                    relatedSourceDocuments = (
                        metadataObj?.['answer_sources'] || []
                    ).map((sd: any, index: number) => {
                        return {
                            content: sd.page_content,
                            metadata: sd.metadata,
                            messageId: latestMessage.rowID,
                            id: latestMessage.rowID + index,
                            tenantModel: model
                        };
                    });
                }

                if (answer.startsWith("Hmm, I'm not sure")) {
                    answer = t('error.not-sure');
                } else if (answer === '__AQ_GENAI_API_ERROR__') {
                    answer = t('error.api-error');
                    sourceDocuments = [];
                    relatedSourceDocuments = [];
                } else {
                    canProvideFeedback = true;
                    if (!chatId) {
                        dispatch(
                            setTriageChatId(
                                metadataObj['chat_id'],
                                selectedModel
                            )
                        );
                        const newChat: SingleChatHistoryData = {
                            chat_id: metadataObj['chat_id'],
                            investigation_id: null,
                            user_email: null,
                            tenant_model: selectedModel || model,
                            question: question,
                            created_at: getTodayDateAndTime(),
                            user_external_id: null,
                            triage_product_id:
                                productDetails?.productIds?.productId,
                            summary: null,
                            model_serial_number: modelSerialNumber
                        };
                        updateChatSummary(
                            question,
                            selectedModel || model,
                            metadataObj['chat_id'],
                            newChat
                        );
                    }
                }

                latestMessage.questionId = condensedQuestion;
                latestMessage.sourceDocuments = sourceDocuments;
                latestMessage.relatedSourceDocuments = relatedSourceDocuments;
                latestMessage.condensedQuestion = condensedQuestion;
                latestMessage.triageLink = metadataObj['triage_link'];
                latestMessage.triageSolutionCount =
                    metadataObj['triage_solution_count'] || 0;
                latestMessage.canProvideFeedback = canProvideFeedback;
                latestMessage.siLink = metadataObj['si_link'];
                latestMessage.noResponse = metadataObj['no_response'];
                latestMessage.chatID = metadataObj['chat_id'];
                latestMessage.rowID = metadataObj['chat_history_line_id'];
                consoleLog('"askStream" response:', latestMessage);
                setIsRegenerateAvailable(true);
                const eventData = {
                    'Row ID': latestMessage.rowID,
                    'Chat History ID': latestMessage.chatID,
                    Question: question ?? '',
                    Answer: latestMessage.message,
                    'Condensed Question': latestMessage.condensedQuestion,
                    'No Response': latestMessage.noResponse
                };
                sendEvent(ANALYTICS_EVENTS.RESPONSE_DISPLAYED, eventData);
                setMessages([...newMessages]);
                setSelectedMessage(latestMessage);
                setIsStreamInProgress(false);
                setIsAskRequestInProgress(false);
                setGenerationType(metadataObj['generation_type']);
                if (scopSettings.displayKeywordDisclaimers) {
                    fetchMessageDisclaimers(
                        question,
                        latestMessage.message,
                        latestMessage.rowID,
                        () => setTypingStarted(false)
                    );
                } else {
                    setTypingStarted(false);
                }
            } else if (__metadata__.length === 0) {
                if (!latestMessage.message) {
                    setSelectedMessage(null);
                }
                setTypingStarted(true);
                latestMessage.message += answer;
                setMessages([...newMessages]);
            }
        }

        askAndStream(
            selectedModel || model,
            question,
            true,
            regenerate,
            regenerate ? generationType : null,
            useOriginalValue,
            selectedProductDetails,
            newResponseDepth,
            false,
            {
                onAskInit: (abortController: AbortController) => {
                    setAbortFunction(abortController);
                    setIsStreamError(false);
                    setIsStreamStopped(false);
                    setIsStreamInProgress(true);
                    setIsAskRequestInProgress(true);
                },
                onError: () => {
                    setIsRegenerateAvailable(true);
                    setIsStreamInProgress(false);
                    setIsAskRequestInProgress(false);
                    setTypingStarted(false);
                    setIsRegenerateAvailable(true);
                    cancelStream();
                    setIsStreamError(true);
                },
                onStreamInit: (
                    reader: ReadableStreamDefaultReader<Uint8Array>
                ) => {
                    setReader(reader);
                    setIsAskRequestInProgress(false);
                },
                handleAnswer
            }
        );
    };

    const regenerateLastAnswer = () => {
        const latestMessage = { ...messages.slice(-1)[0] };
        const latestUserMessage = messages
            .filter((mess) => mess.sender === MessageSenderType.User)
            .slice(-1)[0];
        setHistoryMessages({
            ...historyMessages,
            [messages.length - 1]: historyMessages[messages.length - 1]
                ? [...historyMessages[messages.length - 1], latestMessage]
                : [latestMessage]
        });
        setTypingStarted(false);
        setIsAskRequestInProgress(true);
        setIsStreamInProgress(true);
        setIsStreamStopped(false);
        setIsRegenerateAvailable(false);
        askStream(latestUserMessage.message, null, messages, true);
    };

    const askAgain = (responseDepth?: ResponseDepth) => {
        const latestUserMessage = messages
            .filter((mess) => mess.sender === MessageSenderType.User)
            .slice(-1)[0];
        if (!latestUserMessage || !model || model === t('common.all')) {
            return;
        }
        setTypingStarted(false);
        setIsAskRequestInProgress(true);
        setIsStreamInProgress(true);
        setIsStreamStopped(false);
        setIsRegenerateAvailable(false);
        askStream(
            latestUserMessage.message,
            null,
            messages.slice(0, -1),
            false,
            false,
            '',
            true,
            null,
            responseDepth
        );
    };

    const dummyAskStream = async (
        question: string,
        model: string,
        setMessage: Function,
        setIsStreamInProgress: Function,
        responseDepth: ResponseDepth = null,
        handlers: AskStreamHandlers
    ) => {
        setIsStreamInProgress(true);
        let latestMessage: MessageInfo = {
            chatID: '',
            rowID: '',
            message: '',
            questionId: '',
            sentTime: Date.now().toString(),
            direction: 'incoming',
            sender: MessageSenderType.Aquant,
            sourceDocuments: [],
            relatedSourceDocuments: [],
            condensedQuestion: '',
            canProvideFeedback: false,
            triageLink: null,
            triageSolutionCount: 0,
            siLink: null,
            promptInfo: {},
            model,
            expertDetails: null
        };

        setMessage(latestMessage);

        let __headersData__ = '';
        let __metadata__ = '';
        let metadataReady = false;

        function handleAnswer(answer: string) {
            if (answer.includes(SCOP_ERROR_RESPONSE_MARKER)) {
                setIsStreamInProgress(false);
                if (!latestMessage.message) {
                    latestMessage.message = t('error.stream-error');
                    setMessage({ ...latestMessage });
                }
                return;
            }
            if (
                answer.startsWith(HEADER_MARKER_BEGIN) &&
                answer.includes(HEADER_MARKER_END)
            ) {
                __headersData__ = answer.substring(
                    HEADER_MARKER_BEGIN.length,
                    answer.indexOf(HEADER_MARKER_END)
                );
                let headersdataObj = JSON.parse(__headersData__);
                if (
                    headersdataObj['prompt_info'] &&
                    headersdataObj['prompt_info'].suggested_alternatives
                        ?.length > 0
                ) {
                    latestMessage.promptInfo = headersdataObj['prompt_info'];
                }
                answer = answer.substring(
                    answer.indexOf(HEADER_MARKER_END) +
                        HEADER_MARKER_END.length,
                    answer.length
                );
                latestMessage.expertDetails = headersdataObj['expert_details'];
            }
            if (
                answer.startsWith(METADATA_MARKER_BEGIN) &&
                answer.endsWith(METADATA_MARKER_END)
            ) {
                __metadata__ = answer.substring(
                    METADATA_MARKER_BEGIN.length,
                    answer.length - METADATA_MARKER_END.length
                );
                metadataReady = true;
            } else if (
                answer.includes(METADATA_MARKER_BEGIN) &&
                answer.endsWith(METADATA_MARKER_END)
            ) {
                const partOfAnswer = answer.substring(
                    0,
                    answer.indexOf(METADATA_MARKER_BEGIN)
                );
                latestMessage.message += partOfAnswer;
                setMessage({ ...latestMessage });
                __metadata__ = answer.substring(
                    partOfAnswer.length + METADATA_MARKER_BEGIN.length,
                    answer.length - METADATA_MARKER_END.length
                );
                metadataReady = true;
            } else if (answer.startsWith(METADATA_MARKER_BEGIN)) {
                __metadata__ = answer.substring(METADATA_MARKER_BEGIN.length);
            } else if (answer.includes(METADATA_MARKER_BEGIN)) {
                const partOfAnswer = answer.substring(
                    0,
                    answer.indexOf(METADATA_MARKER_BEGIN)
                );
                __metadata__ = answer.substring(
                    partOfAnswer.length + METADATA_MARKER_BEGIN.length
                );
                latestMessage.message += partOfAnswer;
                setMessage({ ...latestMessage });
            } else if (answer.endsWith(METADATA_MARKER_END)) {
                __metadata__ += answer.substring(
                    0,
                    answer.length - METADATA_MARKER_END.length
                );
                metadataReady = true;
            } else if (__metadata__.length > 0) {
                __metadata__ += answer;
            }

            if (metadataReady) {
                let canProvideFeedback = false;
                let metadataObj = JSON.parse(__metadata__);
                let answer = metadataObj['answer'];
                let condensedQuestion = metadataObj['condensed_question'];
                let sourceDocuments = [];
                let relatedSourceDocuments = [];
                let sdocs = metadataObj['source_documents'];
                latestMessage.rowID = metadataObj['chat_history_line_id'];
                if (
                    (sdocs && sdocs.length > 0) ||
                    (metadataObj?.['answer_sources'] &&
                        metadataObj?.['answer_sources'].length > 0)
                ) {
                    sourceDocuments = (sdocs || []).map(
                        (sd: any, index: number) => {
                            return {
                                content: sd.page_content,
                                metadata: sd.metadata,
                                messageId: latestMessage.rowID,
                                id: latestMessage.rowID + index,
                                tenantModel: model
                            };
                        }
                    );
                    relatedSourceDocuments = (
                        metadataObj?.['answer_sources'] || []
                    ).map((sd: any, index: number) => {
                        return {
                            content: sd.page_content,
                            metadata: sd.metadata,
                            messageId: latestMessage.rowID,
                            id: latestMessage.rowID + index,
                            tenantModel: model
                        };
                    });
                }

                if (answer.startsWith("Hmm, I'm not sure")) {
                    answer = t('error.not-sure');
                } else if (answer === '__AQ_GENAI_API_ERROR__') {
                    answer = t('error.api-error');
                    sourceDocuments = [];
                    relatedSourceDocuments = [];
                } else {
                    canProvideFeedback = true;
                }

                latestMessage.questionId = condensedQuestion;
                latestMessage.sourceDocuments = sourceDocuments;
                latestMessage.relatedSourceDocuments = relatedSourceDocuments;
                latestMessage.condensedQuestion = condensedQuestion;
                latestMessage.triageLink = metadataObj['triage_link'];
                latestMessage.triageSolutionCount =
                    metadataObj['triage_solution_count'] || 0;
                latestMessage.canProvideFeedback = canProvideFeedback;
                latestMessage.siLink = metadataObj['si_link'];
                latestMessage.noResponse = metadataObj['no_response'];
                latestMessage.chatID = metadataObj['chat_id'];
                latestMessage.rowID = metadataObj['chat_history_line_id'];
                consoleLog('"askStream" response:', latestMessage);
                setMessage({ ...latestMessage });
                setIsStreamInProgress(false);
            } else if (__metadata__.length === 0) {
                latestMessage.message += answer;
                setMessage({ ...latestMessage });
            }
        }
        handlers.handleAnswer = handleAnswer;
        askAndStream(
            model,
            question,
            false,
            false,
            null,
            true,
            null,
            responseDepth,
            true,
            handlers
        );
    };

    return {
        askStream,
        regenerateLastAnswer,
        askAgain,
        dummyAskStream
    };
};
